import { SquareCatalogProduct } from "../types/SquareCatalog";

export type ProductGroup = {
  key: string;
  /** Display name, e.g. "Smoke Topaz Glass Rhinestones". */
  name: string;
  products: SquareCatalogProduct[];
  /** Size/option label for each product in the group, keyed by Square variationId. */
  labels: Record<string, string>;
};

type ParsedSize = {
  label: string;
  sortValue: number;
};

/** Rhinestone sizes: "SS10", "ss 16", "SS-20". */
const ssPattern = /\bss\s*-?\s*(\d+)\b/i;
/** Millimeter sizes, with an optional weight: "3 MM (1 oz)", "4mm". */
const mmPattern = /\b(\d+(?:\.\d+)?)\s*mm\b(?:\s*\(([^)]*)\))?/i;
const separators = /^[\s\-–|,/:]+|[\s\-–|,/:]+$/g;

function parseSize(text: string): ParsedSize | null {
  const ss = text.match(ssPattern);
  if (ss) {
    return { label: `SS${ss[1]}`, sortValue: Number(ss[1]) };
  }
  const mm = text.match(mmPattern);
  if (mm) {
    return { label: mm[2] ? `${mm[1]} MM (${mm[2].trim()})` : `${mm[1]} MM`, sortValue: Number(mm[1]) };
  }
  return null;
}

function removeSize(text: string): string {
  return text
    .replace(new RegExp(ssPattern.source, "gi"), " ")
    .replace(new RegExp(mmPattern.source, "gi"), " ")
    .replace(/\(\s*\)/g, " ")
    .replace(/\s*,\s*,/g, ",")
    .replace(separators, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Matching key that ignores punctuation, spacing, and capitalization, so small typos in Square
 * names like "(Gross 1440) Light Siam" vs "(Gross 1440 Light Siam" still land in the same listing.
 */
function groupKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Cleans up unbalanced brackets left over from Square names, e.g. "(Gross 1440 Light" → "(Gross 1440) Light". */
function tidyName(name: string): string {
  const opens = (name.match(/\(/g) ?? []).length;
  const closes = (name.match(/\)/g) ?? []).length;
  if (opens > closes) {
    return name.replace(/\(([^()]*?\d)(\s)/, "($1)$2");
  }
  return name;
}

/**
 * Joins a Square option name with the item name without repeating words.
 * "Neon Blue" + "Neon Silver-Back Rhinestones" → "Neon Blue Silver-Back Rhinestones".
 * If the option already contains the whole item name, the option is used as-is.
 */
function combineNames(variant: string, baseName: string): string {
  const variantWords = new Set(variant.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));
  const baseWords = baseName.split(/\s+/);
  const remaining = baseWords.filter((word) => {
    const normalized = word.toLowerCase().replace(/[^a-z0-9]+/g, "");
    return !normalized || !variantWords.has(normalized);
  });
  const variantLabel = titleCase(variant);
  return remaining.length > 0 ? `${variantLabel} ${remaining.join(" ")}`.trim() : variantLabel;
}

function titleCase(text: string): string {
  return text.replace(/\b([a-z])/g, (letter) => letter.toUpperCase());
}

/** Finds the size in either the product name or the Square variation name. */
function findSize(product: SquareCatalogProduct): ParsedSize | null {
  return parseSize(product.name) ?? parseSize(product.variationName);
}

/** The option label shown for a single product, e.g. "SS12" or its Square variation name. */
export function getSizeLabel(product: SquareCatalogProduct): string {
  return findSize(product)?.label ?? (product.variationName || "Standard");
}

/**
 * Groups the same item across different sizes.
 *
 * Square items are set up two ways, and both are handled:
 * - Size in the variation ("Capri Blue Glass Rhinestones" → variations "SS12", "SS16")
 * - Size in the item name, color in the variation ("Glass Rhinestones SS10" → variation "Smoke topaz")
 *
 * Each group is one color/style, and its options are the sizes. Products without a size
 * (tumblers, Bling Buddies, pens…) always stay as their own listing.
 */
export function groupProducts(products: SquareCatalogProduct[]): ProductGroup[] {
  const groups = new Map<string, ProductGroup & { sortValues: Record<string, number> }>();

  for (const product of products) {
    const size = findSize(product);
    if (!size) {
      const key = `single:${product.variationId}`;
      groups.set(key, { key, name: product.name, products: [product], labels: { [product.variationId]: getSizeLabel(product) }, sortValues: {} });
      continue;
    }

    const baseName = removeSize(product.name) || product.name;
    const variant = removeSize(product.variationName);
    const isDefaultVariant = !variant || /^(regular|standard|default)$/i.test(variant);
    const name = isDefaultVariant ? baseName : combineNames(variant, baseName);
    const key = groupKey(name);

    const existing = groups.get(key);
    const group = existing ?? { key, name: tidyName(name), products: [], labels: {}, sortValues: {} };
    // Prefer the version of the name with balanced brackets for display.
    if (existing && name.includes("(") && (name.match(/\(/g) ?? []).length === (name.match(/\)/g) ?? []).length) {
      group.name = name;
    }
    group.products.push(product);
    group.labels[product.variationId] = size.label;
    group.sortValues[product.variationId] = size.sortValue;
    groups.set(key, group);
  }

  return Array.from(groups.values()).map(({ sortValues, ...group }) => {
    const sorted = [...group.products].sort((a, b) => (sortValues[a.variationId] ?? Infinity) - (sortValues[b.variationId] ?? Infinity));
    // If two options would show the same size, add the Square variation name so customers can tell them apart.
    const counts = new Map<string, number>();
    for (const product of sorted) {
      const label = group.labels[product.variationId];
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    const labels: Record<string, string> = {};
    for (const product of sorted) {
      const label = group.labels[product.variationId];
      labels[product.variationId] = (counts.get(label) ?? 0) > 1 && product.variationName ? `${label} · ${product.variationName}` : label;
    }
    return { ...group, products: sorted, labels };
  });
}

export function formatPrice(priceCents: number | null, currency: string): string {
  if (priceCents === null) {
    return "Price available in Square";
  }
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(priceCents / 100);
}

export function formatPriceRange(products: SquareCatalogProduct[]): string {
  const prices = products.map((product) => product.priceCents).filter((price): price is number => price !== null);
  if (prices.length === 0) {
    return "Price available in Square";
  }
  const currency = products[0].currency;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatPrice(min, currency) : `${formatPrice(min, currency)} – ${formatPrice(max, currency)}`;
}
